<template>
    <div class="scorecard-vendor-summary">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <div
            v-if="loadingInitial" 
            key="loadingInitial"
            class="min-height-300 d-flex justify-content-center align-items-center"
        >
            <busy-spinner text=""></busy-spinner>  <!-- this will be very quick... no text -->
        </div>

        <div 
            v-else 
            key="initialLoadingComplete"
            class=""
        >
            <div
                id="scorecard-summary-header" 
                class="d-flex gap-5 align-items-end justify-content-between mb-3"
            >
                <div class="flex-grow-1">
                    <h3 v-if="systemData && systemData.name" class="text-secondary mb-0" style="padding-bottom: 2px; border-bottom: 2px solid var(--secondary);">
                        {{ systemData.name }}: 
                        <span v-if="vendorName" class="text-primary-darker">{{ vendorName }}</span>
                    </h3>
                </div>

                <div v-if="!hideDatePickers && hasSystemData" class="">
                    <!-- <div class="font-weight-bold mb-1">Date Range</div> -->
                    <div class="d-flex gap-2 align-items-center justify-content-start">
                        <div class="">From</div>
                        <div class="p-field">
                            <label for="start-monthpicker" class="sr-only">Start Date</label>
                            <Calendar 
                                id="start-monthpicker" 
                                v-model="localStartDate" 
                                view="month"
                                dateFormat="MM yy" 
                                :yearNavigator="true" 
                                :minDate="datePickerMinDate"
                                :maxDate="datePickerMaxDate"
                                :manualInput="false"
                                placeholder="Starting month"
                                :disabled="loadingScorecards"
                                inputClass="p-inputtext-sm"
                                @date-select="handleNewDates"
                            >
                                <template #date="{date}">{{date.day}}</template>  <!-- had to manually add the slots default content for some reason with prime-vue components -->
                            </Calendar>
                        </div>
                        <div class="">to</div>
                        <div class="">
                            <label for="end-monthpicker" class="sr-only">End Date</label>
                            <Calendar 
                                id="end-monthpicker" 
                                v-model="localEndDate" 
                                view="month"
                                dateFormat="MM yy" 
                                :yearNavigator="true" 
                                :minDate="datePickerMinDate"
                                :maxDate="datePickerMaxDate"
                                :manualInput="false"
                                placeholder="Ending month"
                                :disabled="loadingScorecards"
                                inputClass="p-inputtext-sm"
                                @date-select="handleNewDates"
                            />
                        </div>
                    </div>
                    <div v-if="dateErrors.length">
                        <div v-for="dateError in dateErrors" :key="dateError" class="text-danger mt-2">
                            {{ dateError }}
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="loadingScorecards"
                class="min-height-300 d-flex justify-content-center align-items-center"
            >
                <busy-spinner text="Loading Scorecards..."></busy-spinner>
            </div>

            <div
                v-else-if="!hasScorecards"
                class="d-flex align-items-center justify-content-center"
            >
                <div class="text-muted font-weight-bold text-uppercase">
                    No Scorecards to show
                </div>
            </div>

            <div v-else-if="hasScorecards" class="scorecard-results">

                <!-- CHART -->
                <div v-if="showChart" class="border rounded-lg bg-white p-3 mt-4 mb-5">
                    <!-- set height on the Chart element itself, then set responsive: true, maintainAspectRatio: false in chartOptions -->
                    <Chart 
                        type="bar" 
                        :data="chartData" 
                        :options="chartOptions" 
                        class="height-300"
                        @select="handleChartSelect"
                    />  
                </div>

                <div v-if="!hideDetails" class="mt-4 mb-2">
                    <h5 class="text-primary-darker d-inline mb-0 mr-3">Details per Period</h5>
                    <span class="text-muted font-italic mb-2">Click on a Period to view details</span>
                </div>

                <scorecard-accordion
                    v-if="!hideDetails"
                    :system-data="systemData"
                    :scorecards="scorecardsDisplayList"
                    title-property="name"
                    class=""
                ></scorecard-accordion>
                
            </div>
        </div>

    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import Calendar from 'primevue/calendar';
//import Knob from 'primevue/knob';
//import CascadeSelect from 'primevue/cascadeselect';
import { suppliosApi } from '../../js/api-service';
import { FormatDateHelper } from '../../js/filters-helpers';
import { DateTime, Duration } from 'luxon';
import Chart from 'primevue/chart';
import orderBy from 'lodash/orderBy';
import ScorecardAccordion from './ScorecardAccordion.vue';


export default {
    name: 'ScorecardVendorSummary',
    components: {
        Calendar,
        //CascadeSelect,
        // Knob,
        Chart,
        ScorecardAccordion,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        systemId: { // ID of the ScorecardSystem -- OR 'summary' for your demo summary/combined scorecard
            type: String,
            required: true,
        },
        vendorId: {
            type: String,
            required: true,
        },
        history: {  // object with possible keys { years, quarters, months, weeks, days, ... } - will use in Luxon calendar math (as a Duration)
            type: Object,
            default: () => ({years: 1}),
        },
        // either use history OR startDate/endDate -- not both -- startDate/endDate win as long as they are both provided and valid (not overlapping)
        startDate: {
            type: Date,
            required: false,
        },
        endDate: {
            type: Date,
            required: false,
        },
        hideDatePickers: {  // set to true if you want to 'hard-code' the dates using 'history' or 'startDate'/'endDate'
            type: Boolean,
            default: false,
        },
        hideChart: {
            type: Boolean,
            default: false,
        },
        hideDetails: {  // hide the details lists
            type: Boolean,
            default: false,
        },
        descending: {  // list scorecards in descending order by date?  latest first
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            // 
            datePickerMinDate: null,  // set once we have ScorecardSystem api data 
            datePickerMaxDate: null,  // set every time we run checkDates -- to always make sure its up to date with current date/time
            dateErrors: [],  // always should be array
            localStartDate: null,
            localEndDate: null,
            // using periods instead of dates
            loadingSystemDetails: false, // are we fetching the list of available periods?
            systemData: null,
            availablePeriods: [],  // flat list of available periods to use
            availablePeriodsNested: [],  // NESTED list of available periods to use, for use in CascadeSelect - instead of datepickers - because datepickers are very annoying... when using in month picker mode, the'll use datetime of local machine, set to 1st of month, but depending on which side of GMT, it may be previous month when formatted as ISO - although you could just force it to be non-timezone aware...
            // scorecard data
            loadingScorecards: false,
            scorecards: [],  // set from API
            vendorName: '',
            // CHARTS
            chartData:{  // we replace this entire object in buildChartData()
                //labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
                datasets: [
                    {
                        label: 'Sample Dataset',
                        backgroundColor: '#42A5F5',
                        data: [65, 59, 80, 81, 56, 55, 40]
                    },
                ],
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,  // default is true, need false so it adjusts to the height you set
                hoverMode: 'index',
                stacked: false,
                plugins: {
                    legend: {
                        display: false,
                    }
                }
                /* scales: {
                    yAxes: [{
                        type: 'linear',
                        display: true,
                        position: 'left',
                        id: 'y-axis-1',
                    }]
                } */
            },
        }
    },
    computed: {
        hasDateErrors() {
            return this.dateErrors?.length > 0;
        },
        /* vendorName() {
            // just get it from the first scorecard, assuming you have it
            const firstScorecard = (!!this.scorecards && this.scorecards.length) ? this.scorecards[0] : undefined;
            return firstScorecard?.vendor?.name;
        }, */
        isMonthly() {
            return this.systemData?.points_period === 'month';
        },
        isQuarterly() {
            return this.systemData?.points_period === 'quarter';
        },
        isYearly() {
            return this.systemData?.points_period === 'year';
        },
        showChart() {
            return !this.hideChart && this.chartData && (this.scorecards.length > 1);  // only show chart if you have more than one scorecard.... chart of one datapoint is stupid
        },
        hasSystemData() {
            return !!this.systemData;
        },
        hasScorecards() {
            return !!this.scorecards?.length;
        },
        loadingInitial() {
            return !this.hasSystemData || this.loadingSystemDetails;
        },
        scorecardsDisplayList() {
            // handle switching the order if needed, etc.
            // not intended (for now) to filter out any... just change order, etc.
            // don't change order of chart -- just the accordians
            const sortOrder = this.descending ? 'desc' : 'asc';
            const sortKey = 'sort_timestamp';  // this should ONLY be used for sorting -- should be approx timestamp of the start of the period (int in seconds)
            return orderBy(this.scorecards, [sortKey], [sortOrder]);
        },
    },
    created() {
        if (this.startDate && this.endDate && (this.startDate < this.endDate)) {
            this.localStartDate = this.startDate;
            this.localEndDate = this.endDate;
        } else if (this.history) {
            const luxonCurrentMonthStart = DateTime.fromISO(DateTime.now().startOf('month').toISODate());  // set to earliest point in this month
            const luxonDuration = Duration.fromObject(this.history);
            const luxonStartDate = luxonCurrentMonthStart.minus(luxonDuration);
            if (luxonStartDate.isValid && luxonCurrentMonthStart.isValid) {
                this.localEndDate = luxonCurrentMonthStart.toJSDate();  // will be based on the 'today' of current user -- the day/month where they are -- which is what we want for these front-end things where we capture user intent
                this.localStartDate = luxonStartDate.toJSDate();
            }
        } // if neither of these worked, what to do?  just do the lookups without any dates
        // now set datePickerMinDate & datePickerMaxDate -- used as the minDate / maxDate on datepickers, so make sure min is BEFORE the localStartDate, and max is AFTER the localEndDate
        // actually... we shouldn't need to (or want to) here -- max/min should only be based on the max/min for the ScorecardSystem -- so need that info first 
        /* if (this.localEndDate) {
            this.datePickerMaxDate = DateTime.fromJSDate(this.localEndDate).plus({minutes: 1}).toJSDate();
        } */
        // NOT NEEDED this.currentDate = new Date();  // always set this AFTER the other dates, since we use it as max date on time/date pickers
    },
    async mounted() {
        await this.getSystemDetails();
        // make sure systemData is loaded first, then you can fire and forget for getScorecards
        this.getScorecards();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        checkDates() { // check dates for errors 
            // TODO - add checks for min/max in case we didn't set those corretly in datepickers?
            // always update max date to current time (plus a second or minute) before we check it
            // actually... would be better to set max date every time the datepicker is 'opened'...
            this.datePickerMaxDate = DateTime.now().plus({ minutes: 1}).toJSDate();
            // we set min date from ScorecardSystem API data
            const errors = [];  // start empty... add if there are any errors, then set this on this.dateErrors
            // you should never get min/max errors if minDate/maxDate are working on datepickers -- they just get 'coerced' to the min/max
            if (this.localStartDate) {
                // make sure localStartDate is AFTER our min possible date 
                if (this.localStartDate < this.datePickerMinDate) {  // must check if its less than, not LTE
                    errors.push(`Starting date must be later than ${this.datePickerMinDate.toDateString()}`);
                }
            }
            if (this.localEndDate) {
                // make sure localEndDate is BEFORE our max possible date (just set above)
                if (this.localEndDate > this.datePickerMaxDate) {
                    errors.push(`Ending date must be before ${this.datePickerMaxDate.toDateString()}`);
                }
            }
            if (this.localStartDate && this.localEndDate) {
                // compare just the dates -- strip off times since they will be different (time will be based on whenever the Date was set)
                const localStartDateOnly = this.getDateOnly(this.localStartDate);
                const localEndDateOnly = this.getDateOnly(this.localEndDate);
                // when you compare them, don't check equality -- they won't be equal (=== or ==) just check if start is greater than end
                if (localStartDateOnly > localEndDateOnly) {
                    errors.push('Starting date cannot be later than ending date');
                }
            }
            this.dateErrors = errors;
            return this.dateErrors;
        },
        handleReset() {
            // reset all
            this.localStartDate = null;
            this.localEndDate = null;
            this.scorecards = [];
        },
        handleNewDates() {
            // call this when any of the datepickers change
            // check they are OK, if so, call getScorecards()
            // wrapping it all in nextTick so new dates are updated before checking / acting on it
            this.$nextTick(() => {
                const dateErrors = this.checkDates();
                if (!dateErrors.length) {  // using return value from checkDates instead of computed, so it is for sure the latest value
                    this.getScorecards();  // reload it
                }
                // if we DID have errors, they should be showing now, so should not need else block
            });
        },
        getDateOnly(dateObj) {
            // helper to strip off the time from a JS Date object - returns a new Date() but with no time set (just 00:00:00 in UTC)
            // used to compare 2 dates -- stripping off times so we only compare the actual date (not time) since that's all we care about here
            return new Date(dateObj.toDateString());
        },
        getGenericDateString(dateObj) {
            // pass in a JS Date object, return a string in format YYYY-MM-DD
            // but it should be naive, NOT have time zone
            // why? because if current date is near beginning/end of period, the actual date might be before/after period cutoff depending on which side of UTC user is on
            // so we want to capture the intent of what the user sees in the date pickers, then pass that to backend... backend will then make real dates from it
            const luxonDate = DateTime.fromJSDate(dateObj);  // will be in UTC
            //console.log('luxonDate', luxonDate);
            const localLuxonDate = luxonDate.toLocal();  // will be in users local timezone
            //console.log('localLuxonDate', localLuxonDate);
            const localDateString = localLuxonDate.toISODate();  // will be YYYY-MM-DD as a string
            //console.log('localDateString', localDateString);
            return localDateString;
        },
        async getSystemDetails() {  // load once on first mounted
            this.loadingSystemDetails = true;
            let resp;
            try {
                // HANDLE YOUR DEMO 'summary" SYSTEM
                if (this.systemId === 'summary') {
                    resp = await suppliosApi.scorecardSystem.get_summary_system_details();
                } else {
                    resp = await suppliosApi.scorecardSystem.get_system_details(this.systemId);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (resp?.data) {
                this.setLocalSystemData(resp.data);
            }
            this.loadingSystemDetails = false;
        },
        setLocalSystemData(sysData) {
            // set systemData and other things from it from api data 
            this.systemData = sysData;
            this.availablePeriods = sysData.available_periods;
            this.availablePeriodsNested = sysData.available_periods_nested;
            // set the datePickerMinDate based on system_start_date -- which is date-only, YYYY-MM-DD format
            // need to set it to datetime, the earliest possible time on users local TZ -- so time of 00:00:00 in local TZ
            const systemStartString = sysData.system_start_date;
            //console.log('systemStartString', systemStartString);
            if (systemStartString) {
                const luxonSystemStart = DateTime.fromISO(systemStartString);  // will have time of 00:00:00 in local TZ since we did not provide time in the original string
                //console.log('luxonSystemStart', luxonSystemStart);
                this.datePickerMinDate = luxonSystemStart.toJSDate();  // TZ should be set... stashing it in data as JS Date for compatibility for use other places
                //console.log('datePickerMinDate', this.datePickerMinDate);
                // this works... sets datePickerMinDate to 00:00:00 in local time... but so does DatePicker when selecting a day/month -- so they might be equal in worst case, but not not conflict if set to same day
            }
        },
        async getScorecards() {  // load every time you change dates, and on initial load -- but only after systemData is loaded!
            this.loadingScorecards = true;
            const vendorId = this.vendorId;
            const axiosConfig = {
                params: {
                    vendor: vendorId,
                    //start: (this.localStartDate ? this.localStartDate.toISOString() : ''),
                    //end: (this.localEndDate ? this.localEndDate.toISOString() : ''),
                    start: (this.localStartDate ? this.getGenericDateString(this.localStartDate) : ''),
                    end: (this.localEndDate ? this.getGenericDateString(this.localEndDate) : ''),
                    // put other filter related things here
                }
            };
            try {
                let scorecardResp;
                // HANDLE YOUR DEMO CASE
                if (this.systemId === 'summary') {
                    scorecardResp = await suppliosApi.scorecardSystem.get_summary_scorecards_for_vendor(axiosConfig);
                } else {
                    scorecardResp = await suppliosApi.scorecardSystem.get_scorecards_for_vendor(this.systemId, axiosConfig);
                }
                this.scorecards = scorecardResp?.data ?? [];
                // set vendor name -- not doing computed so we can keep vendorName when reloading the list of scorecards
                const firstScorecard = (!!this.scorecards && this.scorecards.length) ? this.scorecards[0] : undefined;
                this.vendorName = firstScorecard?.vendor?.name || '';
                this.buildChartData(this.scorecards);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingScorecards = false;
        },
        buildChartData(scorecards) {
            if (scorecards && Array.isArray(scorecards) && scorecards.length) {
                //const labels = scorecards.map(sc => sc.name);
                const values = scorecards.map(sc => sc.final_points);
                const dataset = {
                    label: ([(this.systemData?.name ?? 'Scorecard'), this.systemData.points_name].filter(p => !!p).join(' - ')),  // the name of the 'series', shown when hovered - something like 'Some Scorecard - Points'
                    backgroundColor: '#42A5F5',
                    data: values,
                };
                const chartData = {
                    labels: scorecards.map(sc => sc.name),
                    datasets: [dataset],
                };
                this.chartData = chartData;
            } else {
                this.chartData = null;
            }
        },
        handleChartSelect(payload) {  // payload will be { dataset, element, originalEvent }
            // BELOW IS FROM AnalyticsChart -- replace 
            console.log('handleChartSelect', payload);
            // dataset in payload is just the data array (with some extra stuff) but NOT our dataset object with the added key - so we have to lookup the dataset ourselves from our component instance data
            const { datasetIndex, index } = payload.element;  // index is index of datapoint in that dataset
            const ourDataset = this.chartData.datasets[datasetIndex]; 
            const scorecard = this.scorecards[index];  // the single month or whatever
            //const datasetKey = ourDataset.key;
            const datasetLabel = ourDataset?.label || '';  
            const scorecardLabel = scorecard?.name || '';
            const combinedLabel = [datasetLabel, scorecardLabel].join(' - ');
            const finalPoints = scorecard?.final_points ?? null;
            const items = scorecard?.items || [];  // will we always have objects??? should be ScorecardItem objects. display 'name', 'points', 'internal_url/external_url'
            // then call some function to show a modal with detail...
            // maybe make a component just for displaying list of scorecard items in single scorecard period - use in details section and in modal
            console.log('selected:', combinedLabel, finalPoints, [...items]);

            // FINISH THIS -- show a modal with all the items -- but may need to change layout of the list of items in case there are MANY

            /*
            //const datapointDef = this.datapointDefs[index];
            //console.log('datasetKey', datasetKey);
            //console.log('datapointDef', datapointDef);
            let splitKeyValue = null;  // defaults, then set below based on what type of data you have
            let xAxisValue = null;
            let selectionTitle = '';  // we'll make a descriptive title to pass up about what this selection represents
            if (this.hasXAxis) {
                splitKeyValue = datasetKey; 
                xAxisValue = datapointDef.value;  // datapointDef will be options object { value, label }
                selectionTitle = [datasetLabel, datapointDef.fullLabel].filter(label => !!label).join(' - ');  // remove the empty ones, then join with simple dash for now
            } else {  // if no bins (pie chart) then datasetKey will be undefined?  and datapointDef will be a single series (split using splitKey)
                splitKeyValue = datapointDef.key;  // get series key
                xAxisValue = null;
                selectionTitle = [datasetLabel, datapointDef.fullLabel].filter(label => !!label).join(' - ');  // remove the empty ones, then join with simple dash for now
            }
            const clickedItems = this.getItems(splitKeyValue, xAxisValue);
            //console.log(`CLICKED ON ${clickedItems.length} ITEMS`);
            //console.log(clickedItems);
            // EMIT SOMETHING TO PARENT - just list of IDs for the clickedItems?  Maybe better, so you don't accidentally mutate things... parent will have them already
            const clickedItemIds = clickedItems.map(item => item.id);
            const showDetailParams = {
                itemIds: clickedItemIds,
                title: selectionTitle,
            };
            this.$emit('show-detail', showDetailParams);
            */
        },
        formatDate(value) {
            return FormatDateHelper(value);
        },
        getItemUrl(item) {
            // return the internal_url or external_url depending on currentUserInfo
            return this.$currentUserInfo.is_internal ? item.internal_url : item.external_url;
        },
    }
}
</script>

<style lang="scss">

.supplier-scorecard {
    .p-knob-text {
        font-weight: 700;
    }
}

</style>